import {Pipe, PipeTransform} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {Observable, of} from 'rxjs';
import {map, take} from 'rxjs/operators';

import {isPresent} from '../utils/lang';

/**
 * Pipe that sort translated keys and combination of N keys
 * It takes four parameters.
 *
 * Value - is the array that we want to sort, it can be an array of strings or an array of Objects.
 *
 * Trigger  - is a dumb parameter it is used to trigger the language change ( pure pipes triggers only
 * when one of it parameters change so if you want to trigger a language change in your component you have to
 * change the value of the trigger parameter
 *
 * asc - is a boolean that tell you if you have to do an ascendant order or a descendant one
 * (asc-if is true, desc-if is false).
 * By default this one is true.
 *
 * Paths - is an array of paths to the keys that needs to be translated and on which we do the sort after
 * we added in one key all the translated keys
 *
 * Example: value is [
 * { attributtes:{firstNameTransId: 'Sort', lastNameTransId: 'Test' }}
 * { attributtes:{firstNameTransId: 'Sort', lastNameTransId: 'AnotherTest' }}
 * ]
 *
 * The paths array is ['attributtes.firstNameTransId', 'attributtes.lastNameTransId']
 *
 * in our component we set our trigger false : sortByTranslationsTrigger = true ;
 *
 * in the ngOnInit method of our component do :
 * this.translate.onLangChange.pipe(
 takeUntil(this.componentDestroyed$)
 )
 .subscribe(() => {
      this.triggerSortByTranslationPipe = !this.triggerSortByTranslationPipe;
    });
 *
 * we well call our pipe like this  | sortByTranslations:
 * sortByTranslationsTrigger: ['attributtes.firstNameTransId','attributtes.lastNameTransId'] | asynch
 *
 * and the sort will be made by the translation of  'Sort Test'
 *
 */


@Pipe({
  name: 'sortByTranslations',
  pure: true
})
export class SortByTranslationsPipe implements PipeTransform {

  constructor(private translate: TranslateService) {
  }

  public transform(value: any[],
                   trigger: boolean,
                   paths?: string[],
                   asc = true,
                   enabled = true): Observable<Array<any>> {
    if (!isPresent(value)) {
      return of(null);
    }

    if (!enabled) {
      return of(value);
    }

    if (!Array.isArray(value) && value !== undefined && value !== null) {
      throw new Error(`Invalid value for [SortPipe]: ${value}`);
    }

    if (paths && paths.length > 0 && value.some(element => this.getPathsValues(element, paths) === null)
      || value.length === 0) {
      return of(value);
    }


    return this.translate.get(this.computeNeededTransValues(value, paths))
      .pipe(
        take(1),
        map((translatedValues: string[]) => {
          this.sortByTranslatedValues(value, translatedValues, paths, asc);
          return value;
        })
      );
  }

  public computeNeededTransValues(value: any, paths: string[]): any {
    const neededTransIds: string[] = [];
    value.forEach(element => {
      if (paths && paths.length > 0) {
        paths.forEach(path => {
          neededTransIds.push(this.getElementByPath(element, path.split('.')));
        });
      } else {
        neededTransIds.push(element);
      }
    });
    return neededTransIds;
  }

  public getElementByPath(element: any, splitPath: string[]): any {
    const result = element[splitPath[0]];
    if (splitPath.length === 0) {
      return element;
    } else if (result) {
      return this.getElementByPath(result, splitPath.slice(1));
    } else {
      return null;
    }
  }

  public getPathsValues(element: any, paths: string[]): string[] {
    const pathsValues: string[] = [];
    for (const path of paths) {
      if (this.getElementByPath(element, path.split('.'))) {
        pathsValues.push(this.getElementByPath(element, path.split('.')));
      } else {
        return null;
      }
    }
    return pathsValues;
  }

  public sortByTranslatedValues(value: any, translatedValues: string[], paths: string[], asc: boolean) {
    return value.sort((element1, element2) => {
      const element1TransValues: string = paths && paths.length > 0
        ? this.getPathsValues(element1, paths).map(pathValue => translatedValues[pathValue]).join(' ')
        : translatedValues[element1];

      const element2TransValues: string = paths && paths.length > 0
        ? this.getPathsValues(element2, paths).map(pathValue => translatedValues[pathValue]).join(' ')
        : translatedValues[element2];

      return asc ? element1TransValues.localeCompare(element2TransValues) : element2TransValues.localeCompare(element1TransValues);
    });
  }
}
